import 'shared/utils/react-19-polyfill'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
  ThemeProvider as MuiLegacyThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles'
import { DatadogPagesRouter } from '@datadog/browser-rum-nextjs'
import { AppProps as NextAppProps } from 'next/app'
import Head from 'next/head'
import { useState } from 'react'
import { ReactQueryProvider } from 'shared/services/react-query/provider'
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components'

import 'shared/timber/hydrogen/main/styles.min.css'
import '~/views/global.css'

import { IdleSessionListener } from 'shared/components/idle-session'
import { IntercomProvider } from 'shared/components/intercom/contexts/intercom-provider'
import RouterProgress from 'shared/components/router-progress'
import Analytics from 'shared/services/analytics'
import { Treatments } from 'shared/services/feature-flags'
import { makeIsomorphicClient } from 'shared/services/apollo/utils'
import { FeatureFlagProvider } from 'shared/services/feature-flags/browser'
import { ThemeProvider } from 'shared/timber/theme/context'
import { ToastContainer } from 'shared/timber/v1/Toast'
import { CommonServerSideProps } from 'shared/utils/auth/server'
import { LiveOnV2CheckProvider } from 'shared/utils/project-migration/live-on-v2-check-context'
import { ApolloSessionProvider } from 'shared/utils/auth/v2/client-session-providers'
import { SessionProvider } from 'shared/contexts/session-provider/session-provider'
import { isHexColor } from 'shared/utils/type-guards'

import { WhiteLabelingContext } from '~/common/globalStyles'
import createTheme from '~/common/theme'
import { withAppEmotionCache } from '~/common/emotionPagesDir'
import { OrganizationTypeEnumType } from '~/common/types/graphql'
import NonSupportedBrowserNotice from '~/views/components/NonSupportedBrowserNotice'
import { CurrentUserProvider } from '~/views/contexts/CurrentUserContext'
import { DrawerOpenContextProvider } from '~/views/contexts/DrawerOpenContext'
import { FlashMessageProvider } from '~/views/contexts/FlashMessageContext'
import { MaxAttachmentSizeProvider } from '~/views/contexts/MaxAttachmentSizeContext'
import { StatusChangeReasonProvider } from '~/views/contexts/StatusChangeReasonContext'
import { CookieStorage } from 'shared/utils/storage/cookie-storage'
import { StaticLabelsProvider } from 'shared/components/static-labels-provider'

type PageProps = CommonServerSideProps

const LegacyApp = ({ Component, pageProps }: NextAppProps<PageProps>) => {
  const { sessionData, treatments, ui, labels, userOrgData } = pageProps

  const [theme, updateTheme] = useState(createTheme())
  const [whiteLabelingEnabled, updateWhiteLabeling] = useState(false)

  const handleThemeChange = (newColor: string, whiteLabeled: boolean) => {
    if (!isHexColor(newColor)) return

    if (theme.palette.current !== newColor) {
      updateTheme(createTheme({ palette: { current: newColor } }))
    }

    if (whiteLabeled !== whiteLabelingEnabled) updateWhiteLabeling(whiteLabeled)
  }

  return (
    <>
      <Head>
        <title>GUIDEcx</title>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
        <meta name="theme-color" content="#000000" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="shortcut icon" href="/favicon.ico" />
      </Head>
      <DatadogPagesRouter />
      <Analytics
        user={
          userOrgData
            ? {
                id: userOrgData.id,
                email: userOrgData.email,
                role: userOrgData.role,
                organizationId: userOrgData.organization?.id,
              }
            : undefined
        }
        appVersion={ui?.appVersion}
        workspaceId={userOrgData?.organization?.id}
        memberId={userOrgData?.id}
        isImpersonated={sessionData?._impersonated}
        disableLegacyAccess={treatments?.[Treatments.disableLegacyAccess]}
        planViewMvp={treatments?.[Treatments.planViewMvp]}
      />
      <RouterProgress />
      <IdleSessionListener ignoreRegex="(shared-project|auth\/.+)" />
      <ThemeProvider theme={ui?.theme} storage={new CookieStorage()}>
        <StyledEngineProvider injectFirst>
          <SessionProvider session={sessionData}>
            <ApolloSessionProvider
              pageProps={pageProps}
              client={makeIsomorphicClient(
                sessionData?.accessToken,
                treatments?.['ENG-4852_GraphQL_V2'] ? '2.0' : '1.0',
              )}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FeatureFlagProvider
                  defaultKey={
                    userOrgData?.organization?.id ||
                    OrganizationTypeEnumType.Basic
                  }
                >
                  <DrawerOpenContextProvider>
                    <IntercomProvider>
                      <FlashMessageProvider>
                        <WhiteLabelingContext.Provider
                          value={{
                            whiteLabelingEnabled: whiteLabelingEnabled,
                            toggleTheme: handleThemeChange,
                          }}
                        >
                          <NonSupportedBrowserNotice />

                          <StyledComponentsThemeProvider theme={theme}>
                            <MuiLegacyThemeProvider theme={theme}>
                              <CurrentUserProvider>
                                <MaxAttachmentSizeProvider>
                                  <StatusChangeReasonProvider>
                                    <ReactQueryProvider>
                                      <StaticLabelsProvider
                                        labels={{
                                          common: ui?.commonLabels,
                                          ...labels,
                                        }}
                                      >
                                        <LiveOnV2CheckProvider
                                          commonProps={{
                                            sessionData,
                                            userOrgData,
                                            treatments,
                                          }}
                                        >
                                          <Component {...pageProps} />
                                        </LiveOnV2CheckProvider>
                                      </StaticLabelsProvider>
                                    </ReactQueryProvider>
                                    <ToastContainer />
                                  </StatusChangeReasonProvider>
                                </MaxAttachmentSizeProvider>
                              </CurrentUserProvider>
                            </MuiLegacyThemeProvider>
                          </StyledComponentsThemeProvider>
                        </WhiteLabelingContext.Provider>
                      </FlashMessageProvider>
                    </IntercomProvider>
                  </DrawerOpenContextProvider>
                </FeatureFlagProvider>
              </LocalizationProvider>
            </ApolloSessionProvider>
          </SessionProvider>
        </StyledEngineProvider>
      </ThemeProvider>
    </>
  )
}

export default withAppEmotionCache(LegacyApp)
